import React, { useEffect, useRef, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import FieldWrapper from './FieldWrapper';
import { getMimeTypeFromFileName } from '@/utils/helpers';
import { MODALS } from '@/configs/modals';
import { useModals } from '@/hooks/useModals';

const FormikVideoUpload = (props) => {
  const {
    name,
    label,
    className,
    fieldClassName,
    inputClassName,
    accept = 'video/mp4, video/webm, video/ogg, video/avi, video/mov, video/quicktime, video/x-msvideo, video/x-matroska',
    description,
    maxSizeMB = 500,
  } = props;
  const { modalOpen } = useModals();
  const { t } = useTranslation();
  const [fieldInit] = useField(name);
  const { setFieldValue } = useFormikContext();
  const textDescription = description || t('field_upload_video_text');
  const fileInputRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoURL, setVideoURL] = useState(fieldInit?.value);
  const [error, setError] = useState('');

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validFormats = accept?.split(/\s*,\s*/);
    const extension = file.name?.split('.').pop()?.toLowerCase();
    const validExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'mkv'];
    if (!validFormats?.includes(file.type) && !validExtensions.includes(extension)) {
      setError(
        `❌ ${t('videoUploader.not_valid_file_format')} ${validFormats
          ?.map((type) => type.split('/')[1])
          ?.join(', ')}.`
      );
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(
        `❌ ${t('videoUploader.not_valid_file_size')} — ${maxSizeMB}MB.`
      );
      return;
    }

    setError('');
    setVideoFile(file);
    setVideoURL(URL.createObjectURL(file));
    setFieldValue(name, file);
  };

  const handleInputFileClick = () => {
    fileInputRef.current.click();
  };

  const handleClickRemoveVideo = (e) => {
    e.preventDefault();
    modalOpen(MODALS.confirm, {
      isOpen: true,
      text: t('videoUploader.confirm_delete'),
      isDeleteType: true,
      onOk: () => {
        setError('');
        setVideoFile(null);
        setVideoURL(null);
        setFieldValue(name, null);
      },
    });
  };

  useEffect(() => {
    setVideoURL(fieldInit?.value);
  }, [fieldInit?.value]);

  return (
    <FieldWrapper
      name={name}
      label={label}
      fieldClassName={fieldClassName}
      className={className}
    >
      <div className="video-uploader">
        <div className="video-uploader__inner">
          {!!videoURL ? (
            <div className="video-uploader__preview">
              <video controls className="video-uploader__preview__video">
                <source
                  src={videoURL}
                  type={videoFile?.type || getMimeTypeFromFileName(videoURL)}
                />
                {t('videoUploader.browser_not_support_video')}
              </video>
              <button
                className="video-uploader__btn-remove"
                title={t('videoUploader.btn_remove')}
                onClick={handleClickRemoveVideo}
                type="button"
              >
                ✕
              </button>
            </div>
          ) : (
            <div
              className="video-uploader__empty"
              onClick={handleInputFileClick}
            >
              <input
                type="file"
                name={name}
                accept={accept}
                onChange={handleFileChange}
                className={`video-uploader__input ${inputClassName}`}
                ref={fileInputRef}
              />
              <div className="video-uploader__empty__icon">
                <span className="fa fa-video"></span>
              </div>
              <span>{textDescription}</span>
            </div>
          )}
        </div>
        {!!error && <p className="video-uploader__error mt-2">{error}</p>}

        {!!videoURL && (
          <Button
            color="danger"
            className="waves-effect waves-light mt-3 px-4"
            type="button"
            onClick={handleClickRemoveVideo}
          >
            <span>{t('videoUploader.btn_remove')}</span>
          </Button>
        )}
      </div>
    </FieldWrapper>
  );
};

export default FormikVideoUpload;
